import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  Palette, Download, Shirt, Sparkles, Grid3X3, 
  Star, ShoppingBag, Eye, X, Layers, Type,
  Square, Circle, Triangle, RotateCcw, Printer, Package,
  Truck, CheckCircle, Clock, ArrowRight, Plus, Minus,
  CreditCard, MapPin, Loader2, Upload, Image as ImageIcon, Trash2, Move,
  Save, FolderOpen, Edit2, MoreVertical, Store, DollarSign, Tag, ExternalLink
} from 'lucide-react';

// Custom hook for intersection observer animations
function useReveal(threshold = 0.2) {
  const ref = useRef(null);
  const [isRevealed, setIsRevealed] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsRevealed(true);
        }
      },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, isRevealed };
}

// Modal Component
function Modal({ isOpen, onClose, title, children, size = "default" }) {
  if (!isOpen) return null;
  const sizeClasses = {
    default: "max-w-2xl",
    large: "max-w-4xl",
    xl: "max-w-6xl"
  };
  
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative bg-white border border-border rounded-2xl ${sizeClasses[size]} w-full max-h-[90vh] overflow-auto shadow-2xl`}>
        <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between z-10">
          <h3 className="font-serif font-bold text-xl text-obsidian">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-obsidian transition-colors p-1 hover:bg-mist rounded">
            <X size={20} />
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

// Gallery Section
function GallerySection({ onSelectDesign }) {
  const { ref, isRevealed } = useReveal(0.1);
  const [activeTab, setActiveTab] = useState('All');
  const [previewItem, setPreviewItem] = useState(null);
  const tabs = ['All', 'Graphics', 'Patterns', 'Typography', 'Illustrations'];

  const galleryItems = [
    { name: 'Neon Dreams', image: '/images/studio/hero_bg_neon_city.jpg', category: 'Graphics', views: 1240, downloads: 89 },
    { name: 'Rain Stories', image: '/images/studio/describe_bg_rain_rooftop.jpg', category: 'Illustrations', views: 980, downloads: 67 },
    { name: 'Cyber Room', image: '/images/studio/generate_bg_cyber_room.jpg', category: 'Graphics', views: 1560, downloads: 112 },
    { name: 'Neon Corridor', image: '/images/studio/edit_bg_neon_corridor.jpg', category: 'Patterns', views: 870, downloads: 54 },
    { name: 'City Aerial', image: '/images/studio/publish_bg_city_aerial.jpg', category: 'Graphics', views: 2100, downloads: 156 },
    { name: 'Product Promo', image: '/images/studio/template_thumb_product_promo.jpg', category: 'Typography', views: 650, downloads: 42 },
    { name: 'Event Opener', image: '/images/studio/template_thumb_event_opener.jpg', category: 'Graphics', views: 890, downloads: 78 },
    { name: 'Cinematic Loop', image: '/images/studio/template_thumb_loop.jpg', category: 'Patterns', views: 1100, downloads: 95 },
  ];

  const filteredItems = activeTab === 'All' ? galleryItems : galleryItems.filter(item => item.category === activeTab);

  const handleGetDesign = (item, e) => {
    e.stopPropagation();
    if (onSelectDesign) {
      onSelectDesign(item);
      toast.success(`"${item.name}" added to designer! Scroll down to customize.`);
    }
  };

  const handlePreview = (item, e) => {
    e.stopPropagation();
    setPreviewItem(item);
  };

  return (
    <section ref={ref} className="py-8">
      <div className={`mb-6 transition-all duration-700 ${isRevealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
        <h2 className="font-serif text-2xl font-bold text-obsidian mb-2">Design Gallery</h2>
        <p className="text-muted-foreground">Browse premium designs - click "Get" to use in your T-shirt</p>
      </div>

      <div className={`flex flex-wrap gap-2 mb-6 transition-all duration-700 delay-100 ${isRevealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
        {tabs.map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            data-testid={`gallery-tab-${tab.toLowerCase()}`}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
              activeTab === tab ? 'bg-obsidian text-white' : 'bg-mist text-foreground/70 hover:bg-obsidian/10'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {filteredItems.map((item, index) => (
          <div
            key={item.name}
            data-testid={`gallery-item-${index}`}
            className={`group bg-white rounded-xl border border-border overflow-hidden shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-500 cursor-pointer ${
              isRevealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
            style={{ transitionDelay: `${200 + index * 50}ms` }}
          >
            <div className="aspect-[4/3] overflow-hidden relative">
              <img src={item.image} alt={item.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="absolute bottom-3 left-3 right-3 flex gap-2">
                  <Button 
                    size="sm" 
                    variant="secondary" 
                    className="flex-1 bg-white/90 hover:bg-white text-obsidian text-xs"
                    onClick={(e) => handlePreview(item, e)}
                    data-testid={`preview-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <Eye size={14} className="mr-1" /> Preview
                  </Button>
                  <Button 
                    size="sm" 
                    className="flex-1 bg-gold hover:bg-gold/90 text-obsidian text-xs"
                    onClick={(e) => handleGetDesign(item, e)}
                    data-testid={`get-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <Download size={14} className="mr-1" /> Get
                  </Button>
                </div>
              </div>
            </div>
            <div className="p-3">
              <h3 className="font-medium text-sm text-obsidian mb-1">{item.name}</h3>
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span className="bg-mist px-2 py-0.5 rounded">{item.category}</span>
                <div className="flex items-center gap-2">
                  <span className="flex items-center"><Eye size={12} className="mr-1" />{item.views}</span>
                  <span className="flex items-center"><Download size={12} className="mr-1" />{item.downloads}</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Preview Modal */}
      <Modal isOpen={!!previewItem} onClose={() => setPreviewItem(null)} title={previewItem?.name || 'Preview'} size="large">
        {previewItem && (
          <div className="space-y-4">
            <div className="rounded-lg overflow-hidden">
              <img src={previewItem.image} alt={previewItem.name} className="w-full h-auto" />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <span className="bg-mist px-3 py-1 rounded text-sm">{previewItem.category}</span>
                <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                  <span className="flex items-center"><Eye size={14} className="mr-1" />{previewItem.views} views</span>
                  <span className="flex items-center"><Download size={14} className="mr-1" />{previewItem.downloads} downloads</span>
                </div>
              </div>
              <Button 
                className="bg-gold hover:bg-gold/90 text-obsidian"
                onClick={(e) => { handleGetDesign(previewItem, e); setPreviewItem(null); }}
              >
                <Download size={16} className="mr-2" /> Use This Design
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </section>
  );
}

// Premium Products Section
function PremiumProductsSection() {
  const { ref, isRevealed } = useReveal(0.1);
  const premiumProducts = [
    { name: 'Creator Bundle Pro', price: '$49', originalPrice: '$99', description: '50+ premium templates, fonts & graphics', image: '/images/studio/template_thumb_logo.jpg', rating: 4.9, sales: 1250 },
    { name: 'Social Media Kit', price: '$29', originalPrice: '$59', description: 'Instagram, TikTok & YouTube templates', image: '/images/studio/template_thumb_story.jpg', rating: 4.8, sales: 890 },
    { name: 'Tutorial Pack', price: '$39', originalPrice: '$79', description: 'Complete design tutorial collection', image: '/images/studio/template_thumb_tutorial.jpg', rating: 4.7, sales: 654 },
  ];

  return (
    <section ref={ref} className="py-8">
      <div className={`flex items-center justify-between mb-6 transition-all duration-700 ${isRevealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Star className="text-gold fill-gold" size={20} />
            <h2 className="font-serif text-2xl font-bold text-obsidian">Premium Products</h2>
          </div>
          <p className="text-muted-foreground">Exclusive design resources for serious creators</p>
        </div>
        <Button variant="outline" className="hidden sm:flex" data-testid="view-all-premium">
          View All <ShoppingBag size={16} className="ml-2" />
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {premiumProducts.map((product, index) => (
          <Card
            key={product.name}
            data-testid={`premium-product-${index}`}
            className={`overflow-hidden hover:shadow-xl transition-all duration-500 border-gold/20 ${isRevealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDelay: `${200 + index * 100}ms` }}
          >
            <div className="aspect-video overflow-hidden relative">
              <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
              <div className="absolute top-3 left-3 bg-red-500 text-white px-2 py-1 rounded text-xs font-bold">50% OFF</div>
            </div>
            <CardContent className="p-4">
              <h3 className="font-semibold text-lg text-obsidian mb-1">{product.name}</h3>
              <p className="text-sm text-muted-foreground mb-3">{product.description}</p>
              <div className="flex items-center gap-2 mb-3">
                <div className="flex items-center">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={12} className={i < Math.floor(product.rating) ? 'text-gold fill-gold' : 'text-gray-300'} />
                  ))}
                </div>
                <span className="text-xs text-muted-foreground">{product.rating} ({product.sales} sales)</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-xl font-bold text-obsidian">{product.price}</span>
                  <span className="text-sm text-muted-foreground line-through">{product.originalPrice}</span>
                </div>
                <Button size="sm" className="bg-gold hover:bg-gold/90 text-obsidian">Buy Now</Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </section>
  );
}

// Print-on-Demand Order Modal
function PrintOrderModal({ isOpen, onClose, canvasRef, shirtColor }) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [order, setOrder] = useState(null);
  const [shippingAddress, setShippingAddress] = useState({
    first_name: '', last_name: '', email: '',
    address_line1: '', address_line2: '', city: '',
    state: '', postal_code: '', country: 'US', phone: ''
  });

  useEffect(() => {
    if (isOpen) {
      fetchProducts();
      setStep(1);
      setOrder(null);
    }
  }, [isOpen]);

  const fetchProducts = async () => {
    try {
      const res = await api.get('/gelato/products');
      setProducts(res.data.products || []);
    } catch (err) {
      toast.error('Failed to load products');
    }
  };

  const getCanvasImage = () => {
    if (!canvasRef?.current) return null;
    return canvasRef.current.toDataURL('image/png');
  };

  const handleSelectProduct = (product) => {
    setSelectedProduct(product);
    const matchingVariant = product.variants.find(v => 
      v.colorHex.toLowerCase() === shirtColor.toLowerCase()
    ) || product.variants[0];
    setSelectedVariant(matchingVariant);
  };

  const handleCreateOrder = async () => {
    if (!selectedProduct || !selectedVariant) {
      toast.error('Please select a product and variant');
      return;
    }

    const designBase64 = getCanvasImage();
    if (!designBase64) {
      toast.error('No design to print');
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/gelato/orders/create', {
        product_id: selectedProduct.id,
        variant_id: selectedVariant.id,
        design_base64: designBase64,
        quantity,
        print_area: 'front'
      });
      setOrder(res.data.order);
      setStep(3);
      toast.success('Order created!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create order');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmOrder = async () => {
    if (!order) return;
    
    // Validate shipping
    const required = ['first_name', 'last_name', 'email', 'address_line1', 'city', 'state', 'postal_code'];
    const missing = required.filter(f => !shippingAddress[f]);
    if (missing.length > 0) {
      toast.error(`Please fill in: ${missing.join(', ')}`);
      return;
    }

    setLoading(true);
    try {
      const res = await api.post(`/gelato/orders/${order.id}/confirm`, shippingAddress);
      setOrder({ ...order, ...res.data });
      setStep(4);
      toast.success('Order confirmed!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to confirm order');
    } finally {
      setLoading(false);
    }
  };

  const calculateTotal = () => {
    if (!selectedVariant) return { subtotal: 0, shipping: 0, total: 0 };
    const subtotal = selectedVariant.price * quantity;
    const shipping = subtotal >= 50 ? 0 : 4.99;
    return { subtotal, shipping, total: subtotal + shipping };
  };

  const pricing = calculateTotal();

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Print Your Design" size="large">
      {/* Progress Steps */}
      <div className="flex items-center justify-between mb-8 px-4">
        {[
          { num: 1, label: 'Select Product' },
          { num: 2, label: 'Choose Options' },
          { num: 3, label: 'Shipping' },
          { num: 4, label: 'Confirmation' }
        ].map((s, i) => (
          <div key={s.num} className="flex items-center">
            <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${
              step >= s.num ? 'bg-gold text-obsidian' : 'bg-mist text-muted-foreground'
            }`}>
              {step > s.num ? <CheckCircle size={16} /> : s.num}
            </div>
            <span className={`ml-2 text-sm hidden sm:block ${step >= s.num ? 'text-obsidian' : 'text-muted-foreground'}`}>
              {s.label}
            </span>
            {i < 3 && <ArrowRight size={16} className="mx-4 text-muted-foreground" />}
          </div>
        ))}
      </div>

      {/* Step 1: Select Product */}
      {step === 1 && (
        <div className="space-y-4">
          <p className="text-muted-foreground mb-4">Choose a product to print your design on:</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {products.map(product => (
              <div
                key={product.id}
                onClick={() => { handleSelectProduct(product); setStep(2); }}
                data-testid={`product-${product.id}`}
                className={`border rounded-xl p-4 cursor-pointer transition-all hover:shadow-md ${
                  selectedProduct?.id === product.id ? 'border-gold ring-2 ring-gold/30' : 'border-border'
                }`}
              >
                <div className="aspect-square bg-mist rounded-lg mb-3 flex items-center justify-center overflow-hidden">
                  <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
                </div>
                <h4 className="font-semibold text-obsidian">{product.name}</h4>
                <p className="text-xs text-muted-foreground mb-2">{product.description}</p>
                <p className="text-lg font-bold text-gold">From ${product.basePrice}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Step 2: Choose Variant & Quantity */}
      {step === 2 && selectedProduct && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Preview */}
          <div>
            <h4 className="font-semibold mb-3">Design Preview</h4>
            <div className="bg-mist rounded-xl p-4 flex items-center justify-center">
              <canvas
                ref={canvasRef}
                width={300}
                height={350}
                className="border rounded-lg shadow-lg"
              />
            </div>
          </div>

          {/* Options */}
          <div className="space-y-4">
            <h4 className="font-semibold">{selectedProduct.name}</h4>
            
            <div>
              <Label className="mb-2 block">Color</Label>
              <div className="flex flex-wrap gap-2">
                {[...new Set(selectedProduct.variants.map(v => v.color))].map(color => {
                  const variant = selectedProduct.variants.find(v => v.color === color);
                  return (
                    <button
                      key={color}
                      onClick={() => setSelectedVariant(selectedProduct.variants.find(v => v.color === color && v.size === (selectedVariant?.size || 'M')))}
                      className={`w-10 h-10 rounded-lg border-2 transition-all ${
                        selectedVariant?.color === color ? 'border-gold ring-2 ring-gold/30' : 'border-border'
                      }`}
                      style={{ backgroundColor: variant.colorHex }}
                      title={color}
                    />
                  );
                })}
              </div>
            </div>

            <div>
              <Label className="mb-2 block">Size</Label>
              <div className="flex flex-wrap gap-2">
                {[...new Set(selectedProduct.variants.filter(v => v.color === selectedVariant?.color).map(v => v.size))].map(size => (
                  <button
                    key={size}
                    onClick={() => setSelectedVariant(selectedProduct.variants.find(v => v.color === selectedVariant?.color && v.size === size))}
                    className={`px-4 py-2 rounded-lg border text-sm font-medium transition-all ${
                      selectedVariant?.size === size ? 'border-gold bg-gold/10 text-obsidian' : 'border-border hover:border-gold/50'
                    }`}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="mb-2 block">Quantity</Label>
              <div className="flex items-center gap-3">
                <Button variant="outline" size="sm" onClick={() => setQuantity(Math.max(1, quantity - 1))} disabled={quantity <= 1}>
                  <Minus size={16} />
                </Button>
                <span className="text-lg font-bold w-12 text-center">{quantity}</span>
                <Button variant="outline" size="sm" onClick={() => setQuantity(quantity + 1)}>
                  <Plus size={16} />
                </Button>
              </div>
            </div>

            {/* Pricing Summary */}
            <div className="border-t pt-4 mt-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span>Unit Price:</span>
                <span>${selectedVariant?.price?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Subtotal ({quantity}x):</span>
                <span>${pricing.subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Shipping:</span>
                <span>{pricing.shipping === 0 ? 'FREE' : `$${pricing.shipping.toFixed(2)}`}</span>
              </div>
              <div className="flex justify-between font-bold text-lg border-t pt-2">
                <span>Total:</span>
                <span className="text-gold">${pricing.total.toFixed(2)}</span>
              </div>
              {pricing.subtotal < 50 && (
                <p className="text-xs text-muted-foreground">Add ${(50 - pricing.subtotal).toFixed(2)} more for free shipping!</p>
              )}
            </div>

            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => setStep(1)} className="flex-1">Back</Button>
              <Button onClick={handleCreateOrder} disabled={loading} className="flex-1 bg-gold hover:bg-gold/90 text-obsidian">
                {loading ? <Loader2 className="animate-spin mr-2" size={16} /> : <Package size={16} className="mr-2" />}
                Create Order
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Step 3: Shipping Address */}
      {step === 3 && order && (
        <div className="space-y-6">
          <div className="bg-mist/50 rounded-xl p-4 mb-6">
            <h4 className="font-semibold mb-2">Order Summary</h4>
            <p className="text-sm">{order.product_name} - {order.variant_details.color} / {order.variant_details.size}</p>
            <p className="text-sm">Quantity: {order.quantity}</p>
            <p className="text-lg font-bold text-gold mt-2">Total: ${order.total.toFixed(2)}</p>
          </div>

          <h4 className="font-semibold flex items-center gap-2">
            <MapPin size={18} /> Shipping Address
          </h4>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>First Name *</Label>
              <Input value={shippingAddress.first_name} onChange={e => setShippingAddress({...shippingAddress, first_name: e.target.value})} />
            </div>
            <div>
              <Label>Last Name *</Label>
              <Input value={shippingAddress.last_name} onChange={e => setShippingAddress({...shippingAddress, last_name: e.target.value})} />
            </div>
            <div className="sm:col-span-2">
              <Label>Email *</Label>
              <Input type="email" value={shippingAddress.email} onChange={e => setShippingAddress({...shippingAddress, email: e.target.value})} />
            </div>
            <div className="sm:col-span-2">
              <Label>Address Line 1 *</Label>
              <Input value={shippingAddress.address_line1} onChange={e => setShippingAddress({...shippingAddress, address_line1: e.target.value})} />
            </div>
            <div className="sm:col-span-2">
              <Label>Address Line 2</Label>
              <Input value={shippingAddress.address_line2} onChange={e => setShippingAddress({...shippingAddress, address_line2: e.target.value})} />
            </div>
            <div>
              <Label>City *</Label>
              <Input value={shippingAddress.city} onChange={e => setShippingAddress({...shippingAddress, city: e.target.value})} />
            </div>
            <div>
              <Label>State *</Label>
              <Input value={shippingAddress.state} onChange={e => setShippingAddress({...shippingAddress, state: e.target.value})} />
            </div>
            <div>
              <Label>Postal Code *</Label>
              <Input value={shippingAddress.postal_code} onChange={e => setShippingAddress({...shippingAddress, postal_code: e.target.value})} />
            </div>
            <div>
              <Label>Country</Label>
              <Select value={shippingAddress.country} onValueChange={val => setShippingAddress({...shippingAddress, country: val})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="US">United States</SelectItem>
                  <SelectItem value="CA">Canada</SelectItem>
                  <SelectItem value="GB">United Kingdom</SelectItem>
                  <SelectItem value="DE">Germany</SelectItem>
                  <SelectItem value="AU">Australia</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label>Phone</Label>
              <Input value={shippingAddress.phone} onChange={e => setShippingAddress({...shippingAddress, phone: e.target.value})} />
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <Button variant="outline" onClick={() => setStep(2)} className="flex-1">Back</Button>
            <Button onClick={handleConfirmOrder} disabled={loading} className="flex-1 bg-gold hover:bg-gold/90 text-obsidian">
              {loading ? <Loader2 className="animate-spin mr-2" size={16} /> : <CreditCard size={16} className="mr-2" />}
              Confirm & Pay
            </Button>
          </div>
        </div>
      )}

      {/* Step 4: Confirmation */}
      {step === 4 && order && (
        <div className="text-center py-8">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle size={40} className="text-green-600" />
          </div>
          <h3 className="font-serif text-2xl font-bold text-obsidian mb-2">Order Confirmed!</h3>
          <p className="text-muted-foreground mb-6">Your print order has been submitted successfully.</p>
          
          <div className="bg-mist/50 rounded-xl p-6 text-left max-w-md mx-auto mb-6">
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Order ID:</span>
                <span className="font-mono">{order.gelato_order_id || order.id.slice(0, 8)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Product:</span>
                <span>{order.product_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Estimated Delivery:</span>
                <span>{order.estimated_delivery || '5-7 business days'}</span>
              </div>
              <div className="flex justify-between font-bold pt-2 border-t">
                <span>Total Paid:</span>
                <span className="text-gold">${order.total.toFixed(2)}</span>
              </div>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-left text-sm mb-6">
            <p className="text-amber-800">
              <strong>Note:</strong> This is a MOCK order. To enable real print-on-demand fulfillment, 
              connect your Gelato API key in the backend configuration.
            </p>
          </div>

          <Button onClick={onClose} className="bg-gold hover:bg-gold/90 text-obsidian">
            Done
          </Button>
        </div>
      )}
    </Modal>
  );
}

// T-Shirt Designer Section (Enhanced with Print Order and Image Upload)
function TShirtDesignerSection({ selectedGalleryDesign, onDesignLoaded }) {
  const { ref, isRevealed } = useReveal(0.1);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const designerRef = useRef(null);
  const [shirtColor, setShirtColor] = useState('#FFFFFF');
  const [designText, setDesignText] = useState('Your Design');
  const [textColor, setTextColor] = useState('#000000');
  const [fontSize, setFontSize] = useState(24);
  const [textPosition, setTextPosition] = useState({ x: 150, y: 180 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragTarget, setDragTarget] = useState(null); // 'text' or 'image'
  const [shapes, setShapes] = useState([]);
  const [activeTab, setActiveTab] = useState('text');
  const [showPrintModal, setShowPrintModal] = useState(false);
  
  // Image upload state
  const [uploadedImage, setUploadedImage] = useState(null);
  const [imagePosition, setImagePosition] = useState({ x: 150, y: 160 });
  const [imageSize, setImageSize] = useState(80);
  const [imageOpacity, setImageOpacity] = useState(100);
  const [loadedImage, setLoadedImage] = useState(null);
  const [galleryImageName, setGalleryImageName] = useState(null);
  
  // Save design state
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [designName, setDesignName] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedDesigns, setSavedDesigns] = useState([]);
  const [loadingDesigns, setLoadingDesigns] = useState(true);
  const [showLoadModal, setShowLoadModal] = useState(false);
  
  // Publish to marketplace state
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [publishingDesign, setPublishingDesign] = useState(null);
  const [publishTitle, setPublishTitle] = useState('');
  const [publishDescription, setPublishDescription] = useState('');
  const [publishPrice, setPublishPrice] = useState('');
  const [publishing, setPublishing] = useState(false);
  const navigate = useNavigate();
  
  // Shopify-style Publish Product Modal state
  const [showPublishProductModal, setShowPublishProductModal] = useState(false);
  const [productTitle, setProductTitle] = useState('');
  const [productDescription, setProductDescription] = useState('');
  const [productPrice, setProductPrice] = useState('29.99');
  const [productCategory, setProductCategory] = useState('design');
  const [publishingProduct, setPublishingProduct] = useState(false);

  const productCategories = [
    { value: 'design', label: 'Custom Design' },
    { value: 'apparel', label: 'Apparel' },
    { value: 'art', label: 'Art & Prints' },
    { value: 'accessories', label: 'Accessories' },
  ];

  // Fetch saved designs on mount
  useEffect(() => {
    fetchSavedDesigns();
  }, []);

  // Handle gallery design selection
  useEffect(() => {
    if (selectedGalleryDesign) {
      // Load the gallery image
      setUploadedImage(selectedGalleryDesign.image);
      setGalleryImageName(selectedGalleryDesign.name);
      setDesignText(selectedGalleryDesign.name);
      setImageSize(100); // Start with a good size
      setImagePosition({ x: 150, y: 150 });
      setActiveTab('image');
      
      // Scroll to the designer section
      setTimeout(() => {
        designerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
      
      // Notify parent that design was loaded
      if (onDesignLoaded) onDesignLoaded();
    }
  }, [selectedGalleryDesign, onDesignLoaded]);

  const fetchSavedDesigns = async () => {
    try {
      const res = await api.get('/design-library');
      setSavedDesigns(res.data.designs || []);
      console.log(`Design Library refreshed: ${res.data.count || 0} designs`);
    } catch (err) {
      // No designs yet or error
      console.log('Design Library: No designs or error fetching');
    } finally {
      setLoadingDesigns(false);
    }
  };

  const shirtColors = [
    { name: 'White', value: '#FFFFFF' },
    { name: 'Black', value: '#1a1a1a' },
    { name: 'Navy', value: '#1e3a5f' },
    { name: 'Red', value: '#dc2626' },
    { name: 'Green', value: '#16a34a' },
    { name: 'Gray', value: '#6b7280' },
  ];

  const textColors = ['#000000', '#FFFFFF', '#dc2626', '#16a34a', '#2563eb', '#9333ea', '#f59e0b', '#ec4899'];

  // Load image when uploadedImage changes
  useEffect(() => {
    if (uploadedImage) {
      const img = new Image();
      img.onload = () => setLoadedImage(img);
      img.src = uploadedImage;
    } else {
      setLoadedImage(null);
    }
  }, [uploadedImage]);

  // Draw on canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    
    ctx.clearRect(0, 0, 300, 350);
    
    // Draw T-shirt shape
    ctx.fillStyle = shirtColor;
    ctx.beginPath();
    ctx.moveTo(50, 80); ctx.lineTo(80, 40); ctx.lineTo(120, 40);
    ctx.lineTo(120, 70); ctx.lineTo(180, 70); ctx.lineTo(180, 40);
    ctx.lineTo(220, 40); ctx.lineTo(250, 80); ctx.lineTo(230, 100);
    ctx.lineTo(230, 300); ctx.lineTo(70, 300); ctx.lineTo(70, 100);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = '#ccc';
    ctx.lineWidth = 2;
    ctx.stroke();
    
    // Draw uploaded image
    if (loadedImage) {
      ctx.save();
      ctx.globalAlpha = imageOpacity / 100;
      const aspectRatio = loadedImage.width / loadedImage.height;
      const drawWidth = imageSize;
      const drawHeight = imageSize / aspectRatio;
      ctx.drawImage(
        loadedImage,
        imagePosition.x - drawWidth / 2,
        imagePosition.y - drawHeight / 2,
        drawWidth,
        drawHeight
      );
      ctx.restore();
      
      // Draw selection border when image tab is active
      if (activeTab === 'image') {
        ctx.strokeStyle = '#D4AF37';
        ctx.lineWidth = 2;
        ctx.setLineDash([5, 3]);
        ctx.strokeRect(
          imagePosition.x - drawWidth / 2 - 2,
          imagePosition.y - drawHeight / 2 - 2,
          drawWidth + 4,
          drawHeight + 4
        );
        ctx.setLineDash([]);
      }
    }
    
    // Draw shapes
    shapes.forEach(shape => {
      ctx.fillStyle = shape.color;
      ctx.beginPath();
      if (shape.type === 'circle') ctx.arc(shape.x, shape.y, 20, 0, Math.PI * 2);
      else if (shape.type === 'square') ctx.rect(shape.x - 20, shape.y - 20, 40, 40);
      else if (shape.type === 'triangle') {
        ctx.moveTo(shape.x, shape.y - 20);
        ctx.lineTo(shape.x + 20, shape.y + 20);
        ctx.lineTo(shape.x - 20, shape.y + 20);
      }
      ctx.closePath();
      ctx.fill();
    });
    
    // Draw text
    if (designText) {
      ctx.fillStyle = textColor;
      ctx.font = `bold ${fontSize}px Arial`;
      ctx.textAlign = 'center';
      ctx.fillText(designText, textPosition.x, textPosition.y);
    }
  }, [shirtColor, designText, textColor, fontSize, textPosition, shapes, loadedImage, imagePosition, imageSize, imageOpacity, activeTab]);

  const addShape = (type) => {
    setShapes([...shapes, { type, x: 150, y: 180, color: textColor, id: Date.now() }]);
    toast.success(`${type} added!`);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (event) => {
      setUploadedImage(event.target.result);
      setActiveTab('image');
      toast.success('Image uploaded! Drag to position it.');
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    setUploadedImage(null);
    setLoadedImage(null);
    setImagePosition({ x: 150, y: 160 });
    setImageSize(80);
    setImageOpacity(100);
    if (fileInputRef.current) fileInputRef.current.value = '';
    toast.info('Image removed');
  };

  const handleExportPNG = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = 'tshirt-design.png';
    link.href = canvas.toDataURL('image/png');
    link.click();
    toast.success('Design exported!');
  };

  const handleCanvasMouseDown = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    // Check if clicking on image
    if (loadedImage) {
      const aspectRatio = loadedImage.width / loadedImage.height;
      const drawWidth = imageSize;
      const drawHeight = imageSize / aspectRatio;
      const imgLeft = imagePosition.x - drawWidth / 2;
      const imgRight = imagePosition.x + drawWidth / 2;
      const imgTop = imagePosition.y - drawHeight / 2;
      const imgBottom = imagePosition.y + drawHeight / 2;
      
      if (x >= imgLeft && x <= imgRight && y >= imgTop && y <= imgBottom) {
        setIsDragging(true);
        setDragTarget('image');
        return;
      }
    }
    
    // Check if clicking on text
    if (Math.abs(x - textPosition.x) < 50 && Math.abs(y - textPosition.y) < 20) {
      setIsDragging(true);
      setDragTarget('text');
    }
  };

  const handleCanvasMouseMove = (e) => {
    if (!isDragging) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = Math.max(70, Math.min(230, e.clientX - rect.left));
    const y = Math.max(100, Math.min(280, e.clientY - rect.top));
    
    if (dragTarget === 'image') {
      setImagePosition({ x, y });
    } else if (dragTarget === 'text') {
      setTextPosition({ x, y });
    }
  };

  const handleCanvasMouseUp = () => {
    setIsDragging(false);
    setDragTarget(null);
  };

  const handleSaveDesign = async () => {
    if (!designName.trim()) {
      toast.error('Please enter a design name');
      return;
    }
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    setSaving(true);
    try {
      const previewImage = canvas.toDataURL('image/png');
      
      // Use the new /api/save-design endpoint
      const res = await api.post('/save-design', {
        design_name: designName.trim(),
        preview_image: previewImage,
        product_type: 'tshirt',
        placement_data: {
          shirt_color: shirtColor,
          text_content: designText,
          text_color: textColor,
          font_size: fontSize,
          text_position: textPosition,
          image_position: imagePosition,
          image_size: imageSize,
          image_opacity: imageOpacity,
          has_uploaded_image: !!uploadedImage,
          gallery_image_name: galleryImageName
        }
      });
      
      setShowSaveModal(false);
      setDesignName('');
      
      // Show success message and redirect to Creator Library
      toast.success('Design saved to your Creator Library');
      
      // Redirect to /my-designs after a short delay
      setTimeout(() => {
        navigate('/my-designs');
      }, 500);
      
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save design');
    } finally {
      setSaving(false);
    }
  };

  const handleLoadDesign = (design) => {
    // Load design settings
    setShirtColor(design.shirt_color || '#FFFFFF');
    setDesignText(design.text_content || 'Your Design');
    setTextColor(design.text_color || '#000000');
    setFontSize(design.font_size || 24);
    setTextPosition(design.text_position || { x: 150, y: 180 });
    setImagePosition(design.image_position || { x: 150, y: 160 });
    setImageSize(design.image_size || 80);
    setImageOpacity(design.image_opacity || 100);
    
    // Note: We can't restore the uploaded image data, only the settings
    if (design.has_uploaded_image) {
      toast.info('Note: Re-upload your artwork image to fully restore this design');
    }
    
    setShowLoadModal(false);
    toast.success(`Loaded "${design.name}"`);
  };

  const handleDeleteDesign = async (designId, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this saved design?')) return;
    
    try {
      await api.delete(`/designs/${designId}`);
      toast.success('Design deleted');
      fetchSavedDesigns();
    } catch (err) {
      toast.error('Failed to delete design');
    }
  };

  // Shopify-style: Save as Product (publish to marketplace without Gelato)
  const handleSaveAsProduct = async () => {
    if (!productTitle.trim()) {
      toast.error('Please enter a product title');
      return;
    }
    if (!productPrice || parseFloat(productPrice) <= 0) {
      toast.error('Please enter a valid price');
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) {
      toast.error('No design to publish');
      return;
    }

    setPublishingProduct(true);
    try {
      const previewImage = canvas.toDataURL('image/png');
      
      // Publish directly to marketplace (no Gelato order yet)
      const res = await api.post('/marketplace/publish-product', {
        title: productTitle.trim(),
        description: productDescription.trim() || `Custom ${productCategory} design. High-quality print-on-demand product.`,
        price: parseFloat(productPrice),
        category: productCategory,
        preview_image: previewImage,
        design_data: {
          shirt_color: shirtColor,
          text_content: designText,
          text_color: textColor,
          font_size: fontSize,
          text_position: textPosition,
          image_position: imagePosition,
          image_size: imageSize,
          image_opacity: imageOpacity,
          has_uploaded_image: !!uploadedImage,
          gallery_image_name: galleryImageName
        },
        print_products: ['apparel_tshirt_unisex', 'apparel_hoodie_unisex', 'home_mug']
      });
      
      setShowPublishProductModal(false);
      setProductTitle('');
      setProductDescription('');
      setProductPrice('29.99');
      
      toast.success('Product published to marketplace successfully');
      
      // Navigate to the new product page
      setTimeout(() => {
        navigate(`/products/${res.data.product.id}`);
      }, 500);
      
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to publish product');
    } finally {
      setPublishingProduct(false);
    }
  };

  const handleOpenPublish = (design, e) => {
    e.stopPropagation();
    setPublishingDesign(design);
    setPublishTitle(design.name + ' T-Shirt');
    setPublishDescription(`Custom designed ${design.name} t-shirt. Perfect for expressing your unique style!`);
    setPublishPrice('19.99');
    setShowPublishModal(true);
    setShowLoadModal(false);
  };

  const handlePublishDesign = async () => {
    if (!publishingDesign) return;
    if (!publishTitle.trim()) {
      toast.error('Please enter a title');
      return;
    }
    if (!publishPrice || parseFloat(publishPrice) <= 0) {
      toast.error('Please enter a valid price');
      return;
    }

    setPublishing(true);
    try {
      const res = await api.post(`/designs/${publishingDesign.id}/publish`, {
        design_id: publishingDesign.id,
        title: publishTitle.trim(),
        description: publishDescription.trim(),
        price: parseFloat(publishPrice),
        print_products: ['apparel_tshirt_unisex', 'apparel_hoodie_unisex']
      });
      
      toast.success('Design published to marketplace!');
      setShowPublishModal(false);
      setPublishingDesign(null);
      fetchSavedDesigns();
      
      // Navigate to the product page
      navigate(`/products/${res.data.product.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to publish design');
    } finally {
      setPublishing(false);
    }
  };

  const handleUnpublishDesign = async (designId, e) => {
    e.stopPropagation();
    if (!window.confirm('Remove this design from the marketplace?')) return;
    
    try {
      await api.post(`/designs/${designId}/unpublish`);
      toast.success('Design removed from marketplace');
      fetchSavedDesigns();
    } catch (err) {
      toast.error('Failed to unpublish design');
    }
  };

  const handleReset = () => {
    setDesignText('Your Design');
    setShirtColor('#FFFFFF');
    setTextColor('#000000');
    setFontSize(24);
    setTextPosition({ x: 150, y: 180 });
    setShapes([]);
    handleRemoveImage();
    toast.info('Design reset!');
  };

  return (
    <section ref={(el) => { ref.current = el; designerRef.current = el; }} className="py-8" id="tshirt-designer">
      <div className={`mb-6 transition-all duration-700 ${isRevealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 mb-2">
            <Shirt className="text-gold" size={24} />
            <h2 className="font-serif text-2xl font-bold text-obsidian">Custom T-Shirt Designer</h2>
          </div>
          {galleryImageName && (
            <div className="flex items-center gap-2 bg-gold/10 px-3 py-1 rounded-full">
              <ImageIcon size={14} className="text-gold" />
              <span className="text-sm text-obsidian">Using: {galleryImageName}</span>
            </div>
          )}
        </div>
        <p className="text-muted-foreground">Create your design and order print-on-demand products</p>
      </div>

      <div className={`grid grid-cols-1 lg:grid-cols-2 gap-8 transition-all duration-700 delay-200 ${isRevealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
        {/* Design Canvas */}
        <Card className="overflow-hidden">
          <CardHeader className="bg-mist/50 border-b">
            <CardTitle className="text-lg flex items-center gap-2">
              <Layers size={18} /> Design Preview
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 flex justify-center items-center bg-[repeating-conic-gradient(#f3f4f6_0%_25%,#fff_0%_50%)_50%/20px_20px]">
            <canvas
              ref={canvasRef}
              width={300}
              height={350}
              data-testid="tshirt-canvas"
              className="border border-border rounded-lg bg-white shadow-lg cursor-move"
              onMouseDown={handleCanvasMouseDown}
              onMouseMove={handleCanvasMouseMove}
              onMouseUp={handleCanvasMouseUp}
              onMouseLeave={handleCanvasMouseUp}
            />
          </CardContent>
        </Card>

        {/* Design Controls */}
        <Card>
          <CardHeader className="bg-mist/50 border-b">
            <CardTitle className="text-lg flex items-center gap-2">
              <Palette size={18} /> Design Tools
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="w-full mb-4 grid grid-cols-4">
                <TabsTrigger value="text" className="text-xs sm:text-sm"><Type size={14} className="mr-1 hidden sm:inline" /> Text</TabsTrigger>
                <TabsTrigger value="image" className="text-xs sm:text-sm"><ImageIcon size={14} className="mr-1 hidden sm:inline" /> Image</TabsTrigger>
                <TabsTrigger value="shapes" className="text-xs sm:text-sm"><Square size={14} className="mr-1 hidden sm:inline" /> Shapes</TabsTrigger>
                <TabsTrigger value="colors" className="text-xs sm:text-sm"><Palette size={14} className="mr-1 hidden sm:inline" /> Colors</TabsTrigger>
              </TabsList>

              <TabsContent value="text" className="space-y-4">
                <div>
                  <Label className="mb-2 block">Your Text</Label>
                  <Input value={designText} onChange={(e) => setDesignText(e.target.value)} placeholder="Enter text" data-testid="design-text-input" />
                </div>
                <div>
                  <Label className="mb-2 block">Font Size: {fontSize}px</Label>
                  <input type="range" min="12" max="48" value={fontSize} onChange={(e) => setFontSize(parseInt(e.target.value))} className="w-full accent-gold" />
                </div>
                <div>
                  <Label className="mb-2 block">Text Color</Label>
                  <div className="flex flex-wrap gap-2">
                    {textColors.map(color => (
                      <button key={color} onClick={() => setTextColor(color)} className={`w-8 h-8 rounded-full border-2 transition-all ${textColor === color ? 'border-gold scale-110' : 'border-transparent hover:scale-105'}`} style={{ backgroundColor: color }} />
                    ))}
                  </div>
                </div>
                <p className="text-xs text-muted-foreground flex items-center gap-1"><Move size={12} /> Drag text on canvas to reposition</p>
              </TabsContent>

              <TabsContent value="image" className="space-y-4">
                {!uploadedImage ? (
                  <div>
                    <Label className="mb-2 block">Upload Your Artwork</Label>
                    <div 
                      className="border-2 border-dashed border-border rounded-xl p-6 text-center hover:border-gold/50 transition-colors cursor-pointer"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Upload size={32} className="mx-auto mb-2 text-muted-foreground" />
                      <p className="text-sm font-medium text-obsidian">Click to upload image</p>
                      <p className="text-xs text-muted-foreground mt-1">PNG, JPG, GIF up to 5MB</p>
                    </div>
                    <input 
                      ref={fileInputRef}
                      type="file" 
                      accept="image/*" 
                      onChange={handleImageUpload}
                      className="hidden"
                      data-testid="image-upload-input"
                    />
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-3 p-3 bg-mist/50 rounded-lg">
                      <div className="w-12 h-12 rounded overflow-hidden bg-white border">
                        <img src={uploadedImage} alt="Uploaded" className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-obsidian">Image uploaded</p>
                        <p className="text-xs text-muted-foreground">Drag on canvas to position</p>
                      </div>
                      <Button variant="ghost" size="sm" onClick={handleRemoveImage} className="text-red-500 hover:text-red-600 hover:bg-red-50">
                        <Trash2 size={16} />
                      </Button>
                    </div>
                    
                    <div>
                      <Label className="mb-2 block">Size: {imageSize}px</Label>
                      <input 
                        type="range" 
                        min="30" 
                        max="150" 
                        value={imageSize} 
                        onChange={(e) => setImageSize(parseInt(e.target.value))} 
                        className="w-full accent-gold"
                        data-testid="image-size-slider" 
                      />
                    </div>
                    
                    <div>
                      <Label className="mb-2 block">Opacity: {imageOpacity}%</Label>
                      <input 
                        type="range" 
                        min="10" 
                        max="100" 
                        value={imageOpacity} 
                        onChange={(e) => setImageOpacity(parseInt(e.target.value))} 
                        className="w-full accent-gold"
                        data-testid="image-opacity-slider" 
                      />
                    </div>
                    
                    <Button 
                      variant="outline" 
                      className="w-full"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Upload size={16} className="mr-2" /> Replace Image
                    </Button>
                  </>
                )}
              </TabsContent>

              <TabsContent value="shapes" className="space-y-4">
                <Label className="mb-2 block">Add Shapes</Label>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => addShape('circle')}><Circle size={18} /></Button>
                  <Button variant="outline" onClick={() => addShape('square')}><Square size={18} /></Button>
                  <Button variant="outline" onClick={() => addShape('triangle')}><Triangle size={18} /></Button>
                </div>
              </TabsContent>

              <TabsContent value="colors" className="space-y-4">
                <Label className="mb-2 block">T-Shirt Color</Label>
                <div className="flex flex-wrap gap-3">
                  {shirtColors.map(color => (
                    <button key={color.value} onClick={() => setShirtColor(color.value)} className={`w-10 h-10 rounded-lg border-2 ${shirtColor === color.value ? 'border-gold ring-2 ring-gold/30' : 'border-border'}`} style={{ backgroundColor: color.value }} title={color.name} />
                  ))}
                </div>
              </TabsContent>
            </Tabs>

            <div className="mt-6 pt-4 border-t space-y-3">
              <div className="flex gap-3">
                <Button variant="outline" className="flex-1" onClick={handleReset} data-testid="reset-design-btn">
                  <RotateCcw size={16} className="mr-2" /> Reset
                </Button>
                <Button className="flex-1" onClick={handleExportPNG} data-testid="export-png-btn">
                  <Download size={16} className="mr-2" /> Export PNG
                </Button>
              </div>
              
              {/* Save & Load Buttons */}
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => setShowLoadModal(true)}
                  data-testid="load-design-btn"
                >
                  <FolderOpen size={16} className="mr-2" /> Load
                  {savedDesigns.length > 0 && (
                    <span className="ml-1 bg-gold/20 text-gold text-xs px-1.5 rounded-full">{savedDesigns.length}</span>
                  )}
                </Button>
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => setShowSaveModal(true)}
                  data-testid="save-design-btn"
                >
                  <Save size={16} className="mr-2" /> Save Draft
                </Button>
              </div>
              
              {/* Primary Action: Save as Product */}
              <Button 
                className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold"
                onClick={() => setShowPublishProductModal(true)}
                data-testid="save-as-product-btn"
              >
                <Store size={16} className="mr-2" /> Save as Product
              </Button>
              
              {/* Order Sample (Optional) */}
              <Button 
                variant="outline"
                className="w-full text-muted-foreground hover:text-obsidian"
                onClick={() => setShowPrintModal(true)}
                data-testid="order-print-btn"
              >
                <Printer size={16} className="mr-2" /> Order Sample (Test Print)
              </Button>
              
              <p className="text-xs text-center text-muted-foreground">
                Save as Product to sell on marketplace. Order Sample for personal test prints.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Print Order Modal */}
      <PrintOrderModal 
        isOpen={showPrintModal} 
        onClose={() => setShowPrintModal(false)} 
        canvasRef={canvasRef}
        shirtColor={shirtColor}
      />

      {/* Save Design Modal */}
      <Modal isOpen={showSaveModal} onClose={() => setShowSaveModal(false)} title="Save Design">
        <div className="space-y-4">
          <div>
            <Label className="mb-2 block">Design Name</Label>
            <Input 
              value={designName}
              onChange={(e) => setDesignName(e.target.value)}
              placeholder="My awesome design"
              data-testid="design-name-input"
              autoFocus
            />
          </div>
          
          <div className="bg-mist/50 rounded-lg p-4">
            <p className="text-sm text-muted-foreground mb-2">Preview:</p>
            <div className="flex items-center gap-4">
              <canvas 
                ref={canvasRef} 
                width={100} 
                height={117}
                className="border rounded bg-white" 
                style={{ width: 100, height: 117 }}
              />
              <div>
                <p className="font-medium text-obsidian">{designName || 'Untitled'}</p>
                <p className="text-xs text-muted-foreground">
                  Shirt: {shirtColors.find(c => c.value === shirtColor)?.name || 'Custom'}
                </p>
                {designText && (
                  <p className="text-xs text-muted-foreground">Text: "{designText}"</p>
                )}
              </div>
            </div>
          </div>
          
          <div className="flex gap-3 pt-2">
            <Button variant="outline" className="flex-1" onClick={() => setShowSaveModal(false)}>
              Cancel
            </Button>
            <Button 
              className="flex-1 bg-gold hover:bg-gold/90 text-obsidian"
              onClick={handleSaveDesign}
              disabled={saving}
            >
              {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Save size={16} className="mr-2" />}
              Save Design
            </Button>
          </div>
        </div>
      </Modal>

      {/* Load Design Modal */}
      <Modal isOpen={showLoadModal} onClose={() => setShowLoadModal(false)} title="My Saved Designs" size="large">
        {loadingDesigns ? (
          <div className="flex justify-center py-8">
            <Loader2 className="animate-spin text-gold" size={32} />
          </div>
        ) : savedDesigns.length === 0 ? (
          <div className="text-center py-8">
            <FolderOpen size={48} className="mx-auto mb-4 text-muted-foreground/50" />
            <p className="text-muted-foreground">No saved designs yet</p>
            <p className="text-sm text-muted-foreground mt-1">Create a design and save it to see it here</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {savedDesigns.map((design) => (
              <div
                key={design.id}
                onClick={() => handleLoadDesign(design)}
                className={`group border rounded-xl overflow-hidden cursor-pointer hover:border-gold hover:shadow-md transition-all ${design.published ? 'border-green-200' : ''}`}
                data-testid={`saved-design-${design.id}`}
              >
                <div className="aspect-square bg-mist/50 relative">
                  <img 
                    src={`${process.env.REACT_APP_BACKEND_URL}${design.thumbnail_url}`}
                    alt={design.name}
                    className="w-full h-full object-contain"
                  />
                  {/* Published badge */}
                  {design.published && (
                    <div className="absolute top-2 left-2 bg-green-500 text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                      <Store size={10} /> Listed
                    </div>
                  )}
                  {/* Action buttons */}
                  <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {design.published ? (
                      <button
                        onClick={(e) => handleUnpublishDesign(design.id, e)}
                        className="p-1.5 bg-white/90 rounded-full hover:bg-orange-50"
                        title="Remove from marketplace"
                      >
                        <X size={14} className="text-orange-500" />
                      </button>
                    ) : (
                      <button
                        onClick={(e) => handleOpenPublish(design, e)}
                        className="p-1.5 bg-white/90 rounded-full hover:bg-green-50"
                        title="Sell on marketplace"
                      >
                        <Store size={14} className="text-green-600" />
                      </button>
                    )}
                    <button
                      onClick={(e) => handleDeleteDesign(design.id, e)}
                      className="p-1.5 bg-white/90 rounded-full hover:bg-red-50"
                      title="Delete design"
                    >
                      <Trash2 size={14} className="text-red-500" />
                    </button>
                  </div>
                </div>
                <div className="p-3">
                  <p className="font-medium text-sm text-obsidian truncate">{design.name}</p>
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-muted-foreground">
                      {new Date(design.created_at).toLocaleDateString()}
                    </p>
                    {design.published && (
                      <a 
                        href={`/products/${design.product_id}`}
                        onClick={(e) => e.stopPropagation()}
                        className="text-xs text-green-600 hover:underline flex items-center gap-0.5"
                      >
                        View <ExternalLink size={10} />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Modal>

      {/* Publish Design Modal */}
      <Modal isOpen={showPublishModal} onClose={() => setShowPublishModal(false)} title="Publish to Marketplace">
        {publishingDesign && (
          <div className="space-y-4">
            <div className="flex gap-4 p-4 bg-mist/50 rounded-lg">
              <img 
                src={`${process.env.REACT_APP_BACKEND_URL}${publishingDesign.thumbnail_url}`}
                alt={publishingDesign.name}
                className="w-20 h-20 object-contain border rounded bg-white"
              />
              <div>
                <p className="font-medium text-obsidian">{publishingDesign.name}</p>
                <p className="text-xs text-muted-foreground">Original design</p>
              </div>
            </div>
            
            <div>
              <Label className="mb-2 block">Product Title *</Label>
              <Input 
                value={publishTitle}
                onChange={(e) => setPublishTitle(e.target.value)}
                placeholder="My Custom T-Shirt Design"
                data-testid="publish-title-input"
              />
            </div>
            
            <div>
              <Label className="mb-2 block">Description</Label>
              <Textarea 
                value={publishDescription}
                onChange={(e) => setPublishDescription(e.target.value)}
                placeholder="Describe your design..."
                rows={3}
                data-testid="publish-description-input"
              />
            </div>
            
            <div>
              <Label className="mb-2 block">Price (EUR) *</Label>
              <div className="relative">
                <DollarSign size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input 
                  type="number"
                  min="0.99"
                  step="0.01"
                  value={publishPrice}
                  onChange={(e) => setPublishPrice(e.target.value)}
                  placeholder="19.99"
                  className="pl-9"
                  data-testid="publish-price-input"
                />
              </div>
            </div>
            
            {/* Profit Calculator */}
            <div className="bg-gradient-to-br from-emerald-50 to-green-50 border border-emerald-200 rounded-lg p-4">
              <p className="font-semibold text-emerald-800 mb-2 flex items-center gap-2">
                <DollarSign size={16} className="text-emerald-600" />
                Profit Calculator
              </p>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-emerald-700">Customer pays:</span>
                  <span className="font-medium">€{parseFloat(publishPrice || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-emerald-600">Production cost:</span>
                  <span className="text-red-500">-€12.99</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-emerald-600">Platform fee: <span className="text-[10px] font-bold bg-emerald-500 text-white px-1 rounded">0%</span></span>
                  <span>€0.00</span>
                </div>
                <div className="flex justify-between border-t border-emerald-300 pt-2 mt-2">
                  <span className="font-bold text-emerald-800">Your Profit:</span>
                  <span className={`font-bold text-lg ${parseFloat(publishPrice || 0) - 12.99 > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                    €{Math.max(0, parseFloat(publishPrice || 0) - 12.99).toFixed(2)}
                  </span>
                </div>
              </div>
              {parseFloat(publishPrice || 0) < 15 && (
                <p className="text-xs text-amber-600 mt-2">💡 Set price above €15 for profit</p>
              )}
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setShowPublishModal(false)}>
                Cancel
              </Button>
              <Button 
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                onClick={handlePublishDesign}
                disabled={publishing}
                data-testid="confirm-publish-btn"
              >
                {publishing ? <Loader2 className="animate-spin mr-2" size={16} /> : <Store size={16} className="mr-2" />}
                Publish & Sell
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Shopify-style: Save as Product Modal */}
      <Modal isOpen={showPublishProductModal} onClose={() => setShowPublishProductModal(false)} title="Save as Product" size="large">
        <div className="space-y-5">
          {/* Mockup Preview */}
          <div className="bg-mist/30 rounded-xl p-6 flex flex-col items-center">
            <p className="text-sm font-medium text-muted-foreground mb-3">Product Mockup Preview</p>
            <div className="bg-white rounded-lg shadow-lg p-4 border">
              <canvas 
                ref={canvasRef} 
                width={200} 
                height={233}
                className="rounded" 
                style={{ width: 200, height: 233 }}
              />
            </div>
            <p className="text-xs text-muted-foreground mt-2">This is how your product will appear in the marketplace</p>
          </div>
          
          {/* Product Details Form */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label className="mb-2 block">Product Title *</Label>
              <Input 
                value={productTitle}
                onChange={(e) => setProductTitle(e.target.value)}
                placeholder="e.g., Sunset Beach T-Shirt"
                data-testid="product-title-input"
                autoFocus
              />
            </div>
            
            <div className="md:col-span-2">
              <Label className="mb-2 block">Description</Label>
              <Textarea 
                value={productDescription}
                onChange={(e) => setProductDescription(e.target.value)}
                placeholder="Describe your product... What makes it special? Who is it for?"
                rows={3}
                data-testid="product-description-input"
              />
            </div>
            
            <div>
              <Label className="mb-2 block">Selling Price (EUR) *</Label>
              <div className="relative">
                <DollarSign size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input 
                  type="number"
                  min="9.99"
                  step="0.01"
                  value={productPrice}
                  onChange={(e) => setProductPrice(e.target.value)}
                  placeholder="29.99"
                  className="pl-9"
                  data-testid="product-price-input"
                />
              </div>
            </div>
            
            <div>
              <Label className="mb-2 block">Category</Label>
              <Select value={productCategory} onValueChange={setProductCategory}>
                <SelectTrigger data-testid="product-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {productCategories.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Profit Calculator */}
          <div className="bg-gradient-to-br from-emerald-50 to-green-50 border border-emerald-200 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center">
                <DollarSign size={16} className="text-white" />
              </div>
              <p className="font-semibold text-emerald-800">Profit Calculator</p>
            </div>
            
            <div className="space-y-2 text-sm">
              <div className="flex justify-between items-center py-2 border-b border-emerald-200/50">
                <span className="text-emerald-700">Customer pays:</span>
                <span className="font-semibold text-lg">€{parseFloat(productPrice || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center py-1">
                <span className="text-emerald-600">Production cost:</span>
                <span className="text-red-500">-€12.99</span>
              </div>
              <div className="flex justify-between items-center py-1">
                <span className="text-emerald-600 flex items-center gap-1">
                  Platform fee:
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500 text-white">0%</span>
                </span>
                <span className="text-emerald-600">€0.00</span>
              </div>
              <div className="flex justify-between items-center py-3 border-t-2 border-emerald-300 mt-2">
                <span className="font-bold text-emerald-800">Your Profit per Sale:</span>
                <span className={`font-bold text-2xl ${parseFloat(productPrice || 0) - 12.99 > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                  €{Math.max(0, parseFloat(productPrice || 0) - 12.99).toFixed(2)}
                </span>
              </div>
            </div>
            
            {/* Profit suggestions */}
            {parseFloat(productPrice || 0) < 15 && (
              <div className="mt-3 p-2 bg-amber-100 border border-amber-300 rounded-lg text-xs text-amber-800">
                💡 Tip: Set price above €15 to ensure profit after production costs
              </div>
            )}
            {parseFloat(productPrice || 0) >= 25 && (
              <div className="mt-3 p-2 bg-emerald-100 border border-emerald-300 rounded-lg text-xs text-emerald-800">
                ✨ Great margin! You'll earn €{(parseFloat(productPrice || 0) - 12.99).toFixed(2)} per sale
              </div>
            )}
          </div>
          
          {/* How it works */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm">
            <p className="font-medium text-blue-800 mb-2">How it works:</p>
            <ol className="text-blue-700 text-xs space-y-1 list-decimal list-inside">
              <li>Your product is listed on the marketplace</li>
              <li>A customer finds and purchases your design</li>
              <li>We handle production through Gelato print-on-demand</li>
              <li>Product ships directly to customer</li>
              <li>You receive your profit automatically</li>
            </ol>
          </div>
          
          <div className="flex gap-3 pt-2">
            <Button variant="outline" className="flex-1" onClick={() => setShowPublishProductModal(false)}>
              Cancel
            </Button>
            <Button 
              className="flex-1 bg-green-600 hover:bg-green-700 text-white"
              onClick={handleSaveAsProduct}
              disabled={publishingProduct}
              data-testid="confirm-save-product-btn"
            >
              {publishingProduct ? <Loader2 className="animate-spin mr-2" size={16} /> : <Store size={16} className="mr-2" />}
              Publish to Marketplace
            </Button>
          </div>
        </div>
      </Modal>
    </section>
  );
}

// My Orders Section
function MyOrdersSection() {
  const { ref, isRevealed } = useReveal(0.1);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const res = await api.get('/gelato/orders');
      setOrders(res.data.orders || []);
    } catch (err) {
      // No orders yet
    } finally {
      setLoading(false);
    }
  };

  if (loading) return null;
  if (orders.length === 0) return null;

  const getStatusColor = (status) => {
    const colors = {
      pending_payment: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-blue-100 text-blue-800',
      processing: 'bg-purple-100 text-purple-800',
      shipped: 'bg-green-100 text-green-800',
      delivered: 'bg-green-200 text-green-900'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <section ref={ref} className="py-8">
      <div className={`mb-6 transition-all duration-700 ${isRevealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
        <div className="flex items-center gap-2 mb-2">
          <Package className="text-gold" size={24} />
          <h2 className="font-serif text-2xl font-bold text-obsidian">My Print Orders</h2>
        </div>
        <p className="text-muted-foreground">Track your print-on-demand orders</p>
      </div>

      <div className="space-y-4">
        {orders.map((order, index) => (
          <Card key={order.id} className={`transition-all duration-500 ${isRevealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{ transitionDelay: `${index * 100}ms` }}>
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <h4 className="font-semibold">{order.product_name}</h4>
                  <p className="text-sm text-muted-foreground">
                    {order.variant_details.color} / {order.variant_details.size} x {order.quantity}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Order #{order.gelato_order_id || order.id.slice(0, 8)}
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                    {order.status.replace('_', ' ').toUpperCase()}
                  </span>
                  <span className="font-bold text-gold">${order.total.toFixed(2)}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </section>
  );
}

// Main Page Component
export default function CreatorDesignStudioPage() {
  const [selectedGalleryDesign, setSelectedGalleryDesign] = useState(null);

  const handleSelectGalleryDesign = (design) => {
    setSelectedGalleryDesign(design);
  };

  const handleDesignLoaded = () => {
    // Clear the selection after it's been loaded
    setSelectedGalleryDesign(null);
  };

  const scrollToDesigner = () => {
    document.getElementById('tshirt-designer')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="max-w-7xl mx-auto" data-testid="creator-design-studio-page">
      {/* Hero Header */}
      <div className="relative overflow-hidden rounded-2xl mb-8 bg-gradient-to-br from-obsidian via-obsidian/95 to-obsidian/90">
        <div className="absolute inset-0 opacity-20">
          <img src="/images/studio/hero_bg_neon_city.jpg" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative z-10 p-8 md:p-12">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="text-gold" size={24} />
            <span className="text-gold font-mono text-sm uppercase tracking-wider">Creator Studio</span>
          </div>
          <h1 className="font-serif text-3xl md:text-4xl font-bold text-white mb-3">Design Studio</h1>
          <p className="text-white/70 max-w-xl mb-6">
            Create stunning designs, browse premium templates, and order custom print-on-demand products.
          </p>
          <div className="flex flex-wrap gap-3">
            <Button 
              className="bg-gold hover:bg-gold/90 text-obsidian" 
              data-testid="start-designing-btn"
              onClick={scrollToDesigner}
            >
              <Palette size={16} className="mr-2" /> Start Designing
            </Button>
            <Button variant="outline" className="border-white/30 text-white hover:bg-white/10" data-testid="browse-templates-btn">
              <Grid3X3 size={16} className="mr-2" /> Browse Templates
            </Button>
          </div>
        </div>
      </div>

      <GallerySection onSelectDesign={handleSelectGalleryDesign} />
      <PremiumProductsSection />
      <TShirtDesignerSection 
        selectedGalleryDesign={selectedGalleryDesign}
        onDesignLoaded={handleDesignLoaded}
      />
      <MyOrdersSection />

      {/* Gelato Integration Notice */}
      <section className="py-8 mt-4">
        <Card className="bg-gradient-to-r from-green-50 to-green-100/50 border-green-200">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <Truck size={24} className="text-green-600" />
              </div>
              <div>
                <h3 className="font-serif text-lg font-semibold text-obsidian mb-1 flex items-center gap-2">
                  Print-on-Demand Active
                  <span className="bg-green-500 text-white text-xs px-2 py-0.5 rounded-full">LIVE</span>
                </h3>
                <p className="text-sm text-muted-foreground mb-3">
                  Gelato print fulfillment is connected and ready! Your designs can be printed and shipped worldwide.
                </p>
                <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><CheckCircle size={14} className="text-green-500" /> T-Shirts</span>
                  <span className="flex items-center gap-1"><CheckCircle size={14} className="text-green-500" /> Hoodies</span>
                  <span className="flex items-center gap-1"><CheckCircle size={14} className="text-green-500" /> Mugs</span>
                  <span className="flex items-center gap-1"><CheckCircle size={14} className="text-green-500" /> Posters</span>
                  <span className="flex items-center gap-1"><CheckCircle size={14} className="text-green-500" /> Tote Bags</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>
    </div>
  );
}
